import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Type, Save, RotateCcw, AlertCircle, Check, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { API_BASE_URL } from '../../services/api';

interface HeroTextConfig {
  title: string;
  subtitle: string;
  show_text_default: boolean;
  text_x: number;
  text_y: number;
}

interface HeroTextManagerProps {
  onConfigChange?: (config: HeroTextConfig) => void;
}

const HeroTextManager: React.FC<HeroTextManagerProps> = ({ onConfigChange }) => {
  const { token } = useAuth();
  const [config, setConfig] = useState<HeroTextConfig>({
    title: 'Desvende os Mistérios do Futuro',
    subtitle: 'Consultas de tarot personalizadas com interpretação por inteligência artificial.',
    show_text_default: true,
    text_x: 50,
    text_y: 50,
  });
  const [originalConfig, setOriginalConfig] = useState<HeroTextConfig>(config);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => { loadCurrentConfig(); }, []);

  useEffect(() => {
    const changed =
      config.title !== originalConfig.title ||
      config.subtitle !== originalConfig.subtitle ||
      config.show_text_default !== originalConfig.show_text_default ||
      config.text_x !== originalConfig.text_x ||
      config.text_y !== originalConfig.text_y;
    setHasChanges(changed);
  }, [config, originalConfig]);

  const loadCurrentConfig = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/hero/text-config`);
      const data = await response.json();
      const loaded: HeroTextConfig = {
        title: data.title,
        subtitle: data.subtitle,
        show_text_default: data.show_text_default,
        text_x: data.text_x ?? 50,
        text_y: data.text_y ?? 50,
      };
      setConfig(loaded);
      setOriginalConfig(loaded);
      onConfigChange?.(loaded);
    } catch {
      setMessage({ type: 'error', text: 'Erro ao carregar configurações' });
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    if (!token) return;
    setSaving(true);
    setMessage(null);
    try {
      const response = await fetch(`${API_BASE_URL}/hero/text-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(config),
      });
      const data = await response.json();
      if (response.ok) {
        setOriginalConfig(config);
        onConfigChange?.(config);
        setMessage({ type: 'success', text: data.message || 'Salvo com sucesso!' });
      } else {
        throw new Error(data.detail || 'Erro ao salvar');
      }
    } catch {
      setMessage({ type: 'error', text: 'Erro ao salvar configurações' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  // Cálculo da posição a partir do evento de mouse/touch no preview
  const calcPosition = useCallback((clientX: number, clientY: number) => {
    const rect = previewRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.round(Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100)));
    const y = Math.round(Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100)));
    setConfig(prev => ({ ...prev, text_x: x, text_y: y }));
  }, []);

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    calcPosition(e.clientX, e.clientY);
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => calcPosition(e.clientX, e.clientY);
    const onUp = () => setIsDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [isDragging, calcPosition]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400" />
        <span className="ml-2 text-purple-300">Carregando...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg flex items-center space-x-2 ${message.type === 'success' ? 'bg-green-500/20 border border-green-500/30 text-green-300' : 'bg-red-500/20 border border-red-500/30 text-red-300'}`}
        >
          {message.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span>{message.text}</span>
        </motion.div>
      )}

      <h3 className="text-lg font-semibold text-white flex items-center">
        <Type className="w-5 h-5 mr-2" /> Configurações de Texto do Hero
      </h3>

      <div className="bg-white/5 rounded-lg p-6 border border-white/10 space-y-6">

        {/* Título */}
        <div>
          <label className="block text-sm font-medium text-purple-200 mb-2">Título Principal</label>
          <input
            type="text" value={config.title}
            onChange={e => setConfig({ ...config, title: e.target.value })}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Título principal"
          />
        </div>

        {/* Subtítulo */}
        <div>
          <label className="block text-sm font-medium text-purple-200 mb-2">Subtítulo</label>
          <textarea
            value={config.subtitle}
            onChange={e => setConfig({ ...config, subtitle: e.target.value })}
            rows={3}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
            placeholder="Subtítulo"
          />
        </div>

        {/* Posição do texto — drag */}
        <div>
          <label className="block text-sm font-medium text-purple-200 mb-2">
            Posição do texto — clique ou arraste no preview
          </label>

          {/* Mini-preview arrastável */}
          <div
            ref={previewRef}
            onMouseDown={onMouseDown}
            className="relative w-full rounded-xl overflow-hidden select-none"
            style={{
              aspectRatio: '16/7',
              background: 'linear-gradient(135deg, #1a0a2e 0%, #0f0f23 100%)',
              cursor: isDragging ? 'grabbing' : 'crosshair',
              border: '1px solid rgba(139,92,246,0.4)',
            }}
          >
            {/* Grade de referência */}
            <div className="absolute inset-0 pointer-events-none" style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
              backgroundSize: '10% 14.28%',
            }} />

            {/* Linhas centrais de referência */}
            <div className="absolute inset-y-0 left-1/2 w-px pointer-events-none" style={{ background: 'rgba(139,92,246,0.25)' }} />
            <div className="absolute inset-x-0 top-1/2 h-px pointer-events-none" style={{ background: 'rgba(139,92,246,0.25)' }} />

            {/* Texto arrastável */}
            <div
              className="absolute pointer-events-none"
              style={{
                left: `${config.text_x}%`,
                top: `${config.text_y}%`,
                transform: 'translate(-50%, -50%)',
              }}
            >
              <div className="text-center px-2 py-1 rounded" style={{ background: 'rgba(139,92,246,0.5)', border: '1px solid rgba(139,92,246,0.8)', backdropFilter: 'blur(4px)', minWidth: '80px' }}>
                <div className="text-white text-xs font-bold leading-tight truncate" style={{ maxWidth: '120px' }}>
                  {config.title.split(' ').slice(0, 3).join(' ')}…
                </div>
                <div className="text-purple-200 text-xs leading-tight" style={{ fontSize: '0.55rem' }}>subtítulo</div>
              </div>
              {/* Ponteiro central */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-2 h-2 rounded-full" style={{ background: '#a78bfa', boxShadow: '0 0 6px rgba(167,139,250,0.8)' }} />
              </div>
            </div>

            <div className="absolute bottom-1 right-2 text-xs pointer-events-none" style={{ color: 'rgba(255,255,255,0.3)' }}>
              X: {config.text_x}% · Y: {config.text_y}%
            </div>
          </div>

          {/* Sliders de precisão */}
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <div className="flex justify-between text-xs text-purple-300 mb-1">
                <span>Horizontal (X)</span><span>{config.text_x}%</span>
              </div>
              <input type="range" min={0} max={100} value={config.text_x}
                onChange={e => setConfig({ ...config, text_x: Number(e.target.value) })}
                className="w-full accent-purple-500"
              />
              <div className="flex justify-between text-xs text-white/25 mt-0.5">
                <span>Esquerda</span><span>Centro</span><span>Direita</span>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs text-purple-300 mb-1">
                <span>Vertical (Y)</span><span>{config.text_y}%</span>
              </div>
              <input type="range" min={0} max={100} value={config.text_y}
                onChange={e => setConfig({ ...config, text_y: Number(e.target.value) })}
                className="w-full accent-purple-500"
              />
              <div className="flex justify-between text-xs text-white/25 mt-0.5">
                <span>Topo</span><span>Centro</span><span>Base</span>
              </div>
            </div>
          </div>

          <button
            onClick={() => setConfig({ ...config, text_x: 50, text_y: 50 })}
            className="mt-2 text-xs text-purple-400 hover:text-purple-300 underline"
          >
            Centralizar
          </button>
        </div>

        {/* Mostrar texto por padrão */}
        <div>
          <label className="flex items-center space-x-3 cursor-pointer">
            <input type="checkbox" checked={config.show_text_default}
              onChange={e => setConfig({ ...config, show_text_default: e.target.checked })}
              className="w-5 h-5 text-purple-600 bg-white/10 border-white/20 rounded focus:ring-purple-500"
            />
            <div className="flex items-center space-x-2">
              {config.show_text_default ? <Eye className="w-4 h-4 text-purple-400" /> : <EyeOff className="w-4 h-4 text-purple-400" />}
              <span className="text-purple-200 font-medium">Mostrar texto por padrão</span>
            </div>
          </label>
        </div>
      </div>

      {/* Botões */}
      <div className="flex flex-wrap gap-3">
        <button onClick={saveConfig} disabled={saving || !hasChanges}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${hasChanges && !saving ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-gray-600 text-gray-400 cursor-not-allowed'}`}
        >
          <Save className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} />
          <span>{saving ? 'Salvando...' : 'Salvar Alterações'}</span>
        </button>
        <button onClick={() => setConfig(originalConfig)} disabled={!hasChanges}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${hasChanges ? 'bg-gray-600 hover:bg-gray-700 text-white' : 'bg-gray-700 text-gray-500 cursor-not-allowed'}`}
        >
          <RotateCcw className="w-4 h-4" /><span>Desfazer</span>
        </button>
      </div>
    </div>
  );
};

export default HeroTextManager;
